import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import Modal from './ui/Modal';
import { API_CONFIG } from '../config';
import { apiFetch } from '../utils/api';
import { getCurrencySymbol } from '../utils/currency';

interface ProjectBalance {
  projectId: string;
  projectName?: string;
  totalIncome: number;
  totalExpense: number;
  pendingLiabilities: number;
  taxReserve: number;
  taxRate: number; // Tax rate from project settings
  balance: number; // Net Cash
  currency?: string;
}

interface User {
  id: string;
  username: string;
  role: string;
}

interface BiddingSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  biddingSheetId: string;
  biddingSheet: any;
  onSave: (updatedData: any) => void;
  projectId?: string;
  currency?: string;
}

const BiddingSettingsModal: React.FC<BiddingSettingsModalProps> = ({
  isOpen,
  onClose,
  biddingSheetId,
  biddingSheet,
  onSave,
  projectId,
  currency = 'USD'
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    contractNumber: '',
    paymentDeadline: '',
    paymentDate: '',
    paymentStatus: 'pending',
    status: 'draft',
    vatRate: 0,
    // Management fees
    producerPercentage: 0,
    producerFixedFee: 0,
    useProducerFixedFee: false,
    coordinatorFee: 0,
    coordinatorPercentage: 0,
    useCoordinatorFixedFee: true,
    supervisorFee: 0,
    supervisorPercentage: 0,
    useSupervisorFixedFee: true,
    finderFee: 0,
    finderPercentage: 0,
    useFinderFixedFee: true,
    producerName: '',
    coordinatorName: '',
    supervisorName: '',
    finderName: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [balance, setBalance] = useState<ProjectBalance | null>(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [showReapproveModal, setShowReapproveModal] = useState(false);
  
  // Global defaults for color-coding
  const [globalDefaults, setGlobalDefaults] = useState<Record<string, any>>({});
  
  const [originalFees, setOriginalFees] = useState({
    producerPercentage: 0,
    producerFixedFee: 0,
    useProducerFixedFee: false,
    coordinatorFee: 0,
    coordinatorPercentage: 0,
    useCoordinatorFixedFee: true,
    supervisorFee: 0,
    supervisorPercentage: 0,
    useSupervisorFixedFee: true,
    finderFee: 0,
    finderPercentage: 0,
    useFinderFixedFee: true,
  });

  useEffect(() => {
    if (isOpen && biddingSheet) {
      setFormData({
        invoiceNumber: biddingSheet.invoiceNumber || '',
        contractNumber: biddingSheet.contractNumber || '',
        paymentDeadline: biddingSheet.paymentDeadline ? biddingSheet.paymentDeadline.split('T')[0] : '',
        paymentDate: biddingSheet.paymentDate ? biddingSheet.paymentDate.split('T')[0] : '',
        paymentStatus: biddingSheet.paymentStatus || 'pending',
        status: biddingSheet.status || 'draft',
        vatRate: Number(biddingSheet.vatRate) || 0,
        // Management fees
        producerPercentage: Number(biddingSheet.producerPercentage) || 0,
        producerFixedFee: Number(biddingSheet.producerFixedFee) || 0,
        useProducerFixedFee: biddingSheet.useProducerFixedFee || false,
        coordinatorFee: Number(biddingSheet.coordinatorFee) || 0,
        coordinatorPercentage: Number(biddingSheet.coordinatorPercentage) || 0,
        useCoordinatorFixedFee: biddingSheet.useCoordinatorFixedFee !== false, // default true
        supervisorFee: Number(biddingSheet.supervisorFee) || 0,
        supervisorPercentage: Number(biddingSheet.supervisorPercentage) || 0,
        useSupervisorFixedFee: biddingSheet.useSupervisorFixedFee !== false, // default true
        finderFee: Number(biddingSheet.finderFee) || 0,
        finderPercentage: Number(biddingSheet.finderPercentage) || 0,
        useFinderFixedFee: biddingSheet.useFinderFixedFee !== false, // default true
        producerName: biddingSheet.producerName || '',
        coordinatorName: biddingSheet.coordinatorName || '',
        supervisorName: biddingSheet.supervisorName || '',
        finderName: biddingSheet.finderName || '',
      });

      // Save original fees to detect changes
      setOriginalFees({
        producerPercentage: Number(biddingSheet.producerPercentage) || 0,
        producerFixedFee: Number(biddingSheet.producerFixedFee) || 0,
        useProducerFixedFee: biddingSheet.useProducerFixedFee || false,
        coordinatorFee: Number(biddingSheet.coordinatorFee) || 0,
        coordinatorPercentage: Number(biddingSheet.coordinatorPercentage) || 0,
        useCoordinatorFixedFee: biddingSheet.useCoordinatorFixedFee !== false,
        supervisorFee: Number(biddingSheet.supervisorFee) || 0,
        supervisorPercentage: Number(biddingSheet.supervisorPercentage) || 0,
        useSupervisorFixedFee: biddingSheet.useSupervisorFixedFee !== false,
        finderFee: Number(biddingSheet.finderFee) || 0,
        finderPercentage: Number(biddingSheet.finderPercentage) || 0,
        useFinderFixedFee: biddingSheet.useFinderFixedFee !== false,
      });

      if (biddingSheet.projectId) {
        loadBalance(biddingSheet.projectId);
      }
      
      loadUsers();
      loadGlobalDefaults();
    }
  }, [isOpen, biddingSheet]);

  const loadGlobalDefaults = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/rates/defaults`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGlobalDefaults(data);
      }
    } catch (error) {
      console.error('Error loading global defaults:', error);
    }
  };

  // Helper to check if value differs from global default
  const isCustomValue = (key: string, currentValue: number | boolean): boolean => {
    const defaultKey = `default_${key}`;
    const globalValue = globalDefaults[defaultKey];
    if (globalValue === undefined) return false;
    return currentValue !== globalValue;
  };

  // Get input border class based on whether value is custom
  const getInputClass = (key: string, currentValue: number | boolean, baseClass: string): string => {
    const isCustom = isCustomValue(key, currentValue);
    if (isCustom) {
      return baseClass.replace('border-gray-700', 'border-amber-500/50 ring-1 ring-amber-500/30');
    }
    return baseClass;
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      // Use management endpoint - returns only management users (no admin required)
      const response = await apiFetch(`${API_CONFIG.API_URL}/auth/users/management`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(Array.isArray(data) ? data : []);
      } else {
        console.error('Error loading management users:', response.status, response.statusText);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    }
  };

  const loadBalance = async (projectId: string) => {
    setIsLoadingBalance(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await apiFetch(
        `${API_CONFIG.API_URL}/transactions/project/${projectId}/balance`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBalance(data);
      } else {
        console.error('Failed to load balance');
      }
    } catch (error) {
      console.error('Error loading balance:', error);
    } finally {
      setIsLoadingBalance(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Check if management fees have changed
  const feesHaveChanged = () => {
    const currentProducerFee = formData.useProducerFixedFee ? formData.producerFixedFee : formData.producerPercentage;
    const originalProducerFee = originalFees.useProducerFixedFee ? originalFees.producerFixedFee : originalFees.producerPercentage;
    
    const currentCoordinatorFee = formData.useCoordinatorFixedFee ? formData.coordinatorFee : formData.coordinatorPercentage;
    const originalCoordinatorFee = originalFees.useCoordinatorFixedFee ? originalFees.coordinatorFee : originalFees.coordinatorPercentage;
    
    const currentSupervisorFee = formData.useSupervisorFixedFee ? formData.supervisorFee : formData.supervisorPercentage;
    const originalSupervisorFee = originalFees.useSupervisorFixedFee ? originalFees.supervisorFee : originalFees.supervisorPercentage;
    
    const currentFinderFee = formData.useFinderFixedFee ? formData.finderFee : formData.finderPercentage;
    const originalFinderFee = originalFees.useFinderFixedFee ? originalFees.finderFee : originalFees.finderPercentage;
    
    return (
      currentProducerFee !== originalProducerFee ||
      formData.useProducerFixedFee !== originalFees.useProducerFixedFee ||
      currentCoordinatorFee !== originalCoordinatorFee ||
      formData.useCoordinatorFixedFee !== originalFees.useCoordinatorFixedFee ||
      currentSupervisorFee !== originalSupervisorFee ||
      formData.useSupervisorFixedFee !== originalFees.useSupervisorFixedFee ||
      currentFinderFee !== originalFinderFee ||
      formData.useFinderFixedFee !== originalFees.useFinderFixedFee
    );
  };

  const handleSubmit = async () => {
    // If fees changed and sheet is approved, show confirmation modal
    if (feesHaveChanged() && biddingSheet?.status === 'approved') {
      setShowReapproveModal(true);
      return;
    }
    
    await saveSettings(false);
  };

  const saveSettings = async (updateFinances: boolean) => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Prepare payload with sanitized values
      const payload = {
        invoiceNumber: formData.invoiceNumber,
        contractNumber: formData.contractNumber,
        paymentDeadline: formData.paymentDeadline || null,
        paymentDate: formData.paymentDate || null,
        paymentStatus: formData.paymentStatus,
        status: formData.status,
        vatRate: Number(formData.vatRate),
        // Management fees - always save both values to allow switching modes
        producerPercentage: Number(formData.producerPercentage) || 0,
        producerFixedFee: Number(formData.producerFixedFee) || 0,
        useProducerFixedFee: formData.useProducerFixedFee,
        coordinatorFee: Number(formData.coordinatorFee) || 0,
        coordinatorPercentage: Number(formData.coordinatorPercentage) || 0,
        useCoordinatorFixedFee: formData.useCoordinatorFixedFee,
        supervisorFee: Number(formData.supervisorFee) || 0,
        supervisorPercentage: Number(formData.supervisorPercentage) || 0,
        useSupervisorFixedFee: formData.useSupervisorFixedFee,
        finderFee: Number(formData.finderFee) || 0,
        finderPercentage: Number(formData.finderPercentage) || 0,
        useFinderFixedFee: formData.useFinderFixedFee,
        producerName: formData.producerName || null,
        coordinatorName: formData.coordinatorName || null,
        supervisorName: formData.supervisorName || null,
        finderName: formData.finderName || null,
      };

      const response = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const updatedSheet = await response.json();
        
        // Update local state with response data
        setFormData(prev => ({
          ...prev,
          supervisorName: updatedSheet.supervisorName || '',
          producerName: updatedSheet.producerName || '',
          coordinatorName: updatedSheet.coordinatorName || '',
          finderName: updatedSheet.finderName || '',
          supervisorFee: Number(updatedSheet.supervisorFee) || 0,
          supervisorPercentage: Number(updatedSheet.supervisorPercentage) || 0,
          useSupervisorFixedFee: updatedSheet.useSupervisorFixedFee !== false,
          finderFee: Number(updatedSheet.finderFee) || 0,
          finderPercentage: Number(updatedSheet.finderPercentage) || 0,
          useFinderFixedFee: updatedSheet.useFinderFixedFee !== false,
          coordinatorFee: Number(updatedSheet.coordinatorFee) || 0,
          coordinatorPercentage: Number(updatedSheet.coordinatorPercentage) || 0,
          useCoordinatorFixedFee: updatedSheet.useCoordinatorFixedFee !== false,
          producerPercentage: Number(updatedSheet.producerPercentage) || 0,
          producerFixedFee: Number(updatedSheet.producerFixedFee) || 0,
          useProducerFixedFee: updatedSheet.useProducerFixedFee || false
        }));
        
        // If updateFinances is true, trigger re-approval of expense assignments
        if (updateFinances && biddingSheet?.projectId) {
          await reapproveExpenseAssignments();
        }
        
        onSave(updatedSheet);
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to save settings:', errorData);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
      setShowReapproveModal(false);
    }
  };

  const reapproveExpenseAssignments = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      
      // Get all expense assignments for this bidding sheet
      const shotsResponse = await fetch(`${API_CONFIG.API_URL}/bidding/${biddingSheetId}/shots`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!shotsResponse.ok) return;
      
      const shotsData = await shotsResponse.json();
      const allAssignmentIds: string[] = [];
      
      // Collect all expense assignment IDs
      for (const shot of (shotsData.shots || [])) {
        if (shot.expenseAssignments) {
          for (const assignment of shot.expenseAssignments) {
            if (assignment.id) {
              allAssignmentIds.push(assignment.id);
            }
          }
        }
      }
      
      if (allAssignmentIds.length === 0) return;
      
      // Call bulk approve with updateFees flag
      await fetch(`${API_CONFIG.API_URL}/bidding/expense-assignments/bulk-approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          assignmentIds: allAssignmentIds,
          biddingSheetId: biddingSheetId,
          updateFees: true // This flag tells backend to update existing fee liabilities
        })
      });
    } catch (error) {
      console.error('Error re-approving expense assignments:', error);
    }
  };

  const renderBalanceInfo = () => {
    if (!balance && !isLoadingBalance) return null;
    if (isLoadingBalance) return <div className="text-sm text-gray-400 py-2">{t('common.loading')}</div>;

    return (
      <div className="bg-gray-900/30 rounded-lg p-3 mb-4 border border-gray-700">
        <h4 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">
          {t('transactions.balance') || 'Project Balance'}
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-0.5">{t('transactions.totalGrossIncome')}</div>
            <div className="text-sm font-bold text-green-400">
              {getCurrencySymbol(balance?.currency)}{balance?.totalIncome.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-0.5">{t('transactions.pendingLiabilities')}</div>
            <div className="text-sm font-bold text-red-400">
              {getCurrencySymbol(balance?.currency)}{balance?.pendingLiabilities.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-0.5">{t('transactions.taxReserve')}</div>
            <div className="text-sm font-bold text-yellow-400">
              {getCurrencySymbol(balance?.currency)}{balance?.taxReserve.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-0.5">{t('transactions.netCashAvailable')}</div>
            <div className={`text-sm font-bold ${
              (balance?.balance || 0) >= 0 ? 'text-blue-400' : 'text-red-500'
            }`}>
              {getCurrencySymbol(balance?.currency)}{balance?.balance.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('bidding.settings') || 'Bidding Settings'}
      size="lg"
    >
      <div className="space-y-4">
        {/* Financial Settings */}
        <div className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg space-y-3">
          <h3 className="text-sm font-semibold text-gray-300 border-b border-gray-700 pb-2">
            {t('bidding.financialSettings') || 'Financial Settings'}
          </h3>
          
          {renderBalanceInfo()}

          {/* Tax Rate Info from Project */}
          {balance && balance.taxRate > 0 && (
            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="text-sm text-yellow-400">
                  {t('bidding.projectTaxRate') || 'Налоговая ставка проекта'}: <strong>{balance.taxRate}%</strong>
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {t('bidding.projectTaxRateHint') || 'Налог рассчитывается автоматически на все поступления'}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                {t('bidding.vat')} (%)
                {isCustomValue('vat_rate', formData.vatRate) && (
                  <span className="ml-2 text-amber-400 text-[10px]" title={`${t('bidding.globalDefault') || 'Глобальное'}: ${globalDefaults.default_vat_rate ?? 0}%`}>
                    [кастомное]
                  </span>
                )}
              </label>
                <input
                type="number"
                name="vatRate"
                min="0"
                max="100"
                step="0.1"
                value={formData.vatRate || ''}
                onChange={handleChange}
                placeholder="0"
                className={getInputClass('vat_rate', formData.vatRate, "w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:border-indigo-500 outline-none")}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('bidding.statusLabel') || 'Статус сметы'}</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:border-indigo-500 outline-none"
              >
                <option value="draft">{t('bidding.statuses.draft') || 'Черновик'}</option>
                <option value="sent">{t('bidding.statuses.sent') || 'На согласовании'}</option>
                <option value="approved">{t('bidding.statuses.approved') || 'Утвержден'}</option>
                <option value="rejected">{t('bidding.statuses.rejected') || 'Отклонен'}</option>
                <option value="hidden">{t('bidding.statuses.hidden') || 'Скрыт'}</option>
              </select>
            </div>
          </div>

          {projectId && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                {t('bidding.projectCurrency') || 'Project currency'}
              </label>
              <select
                value={balance?.currency || biddingSheet?.projectCurrency || biddingSheet?.currency || 'USD'}
                disabled
                className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded text-gray-300 cursor-not-allowed outline-none"
              >
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="RUB">RUB - Russian Ruble</option>
                <option value="BYN">BYN - Belarusian Ruble</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                {t('bidding.projectCurrencyHint') || 'Inherited from project. Change in project settings to update all estimates.'}
              </p>
            </div>
          )}
        </div>

        {/* Management Fees - 2x2 Grid */}
        <div className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
          <div className="flex items-center justify-between border-b border-gray-700 pb-2 mb-4">
            <h3 className="text-sm font-semibold text-gray-300">
              {t('bidding.managementFees') || 'Гонорары управления'}
            </h3>
            <div className="flex items-center gap-4 text-[10px]">
              <span className="flex items-center gap-1 text-gray-500">
                <span className="w-3 h-3 rounded border border-gray-700 bg-gray-900"></span>
                {t('bidding.globalValue') || 'Глобальное'}
              </span>
              <span className="flex items-center gap-1 text-amber-400">
                <span className="w-3 h-3 rounded border border-amber-500/50 bg-gray-900 ring-1 ring-amber-500/30"></span>
                {t('bidding.customValue') || 'Кастомное'}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
          {/* Producer */}
            <div className="p-3 bg-gray-900/50 rounded-lg border border-gray-700/50 space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('bidding.producerName') || 'Имя продюсера'}:</label>
              <select
                value={formData.producerName}
                onChange={(e) => setFormData(prev => ({ ...prev, producerName: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-indigo-500 outline-none"
              >
                <option value="">{t('common.notAssigned') || 'Не назначен'}</option>
                {(() => {
                  const filteredUsers = users.filter(u => ['Super Admin', 'Admin', 'Producer', 'Sales', 'Coordinator'].includes(u.role));
                  const isInList = filteredUsers.some(u => u.username === formData.producerName);
                  return (
                    <>
                      {formData.producerName && !isInList && (
                        <option value={formData.producerName}>{formData.producerName} ✓</option>
                      )}
                      {filteredUsers.map(user => (
                        <option key={user.id} value={user.username}>{user.username}</option>
                      ))}
                    </>
                  );
                })()}
              </select>
            </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-400">
                  {t('bidding.producerFee') || 'Гонорар продюсера'}:
                  {(isCustomValue('producer_percentage', formData.producerPercentage) || isCustomValue('use_producer_fixed_fee', formData.useProducerFixedFee)) && (
                    <span className="ml-1 text-amber-400 text-[10px]">[кастом]</span>
                  )}
                </label>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, useProducerFixedFee: !prev.useProducerFixedFee }))}
                  className={`text-xs px-2 py-0.5 rounded ${formData.useProducerFixedFee ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                >
                    {formData.useProducerFixedFee ? t('bidding.fixedAmount') || 'Фикс.' : '%'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step={formData.useProducerFixedFee ? "0.01" : "0.1"}
                    max={formData.useProducerFixedFee ? undefined : 100}
                    value={(formData.useProducerFixedFee ? formData.producerFixedFee : formData.producerPercentage) || ''}
                    placeholder="0"
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      [formData.useProducerFixedFee ? 'producerFixedFee' : 'producerPercentage']: parseFloat(e.target.value) || 0 
                    }))}
                    className={getInputClass(
                      formData.useProducerFixedFee ? 'producer_fixed_fee' : 'producer_percentage',
                      formData.useProducerFixedFee ? formData.producerFixedFee : formData.producerPercentage,
                      "flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm text-center focus:border-indigo-500 outline-none"
                    )}
                  />
                  <span className="text-gray-400 text-sm w-12">{formData.useProducerFixedFee ? currency : '%'}</span>
              </div>
            </div>
          </div>

          {/* Coordinator */}
            <div className="p-3 bg-gray-900/50 rounded-lg border border-gray-700/50 space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('bidding.coordinatorName') || 'Координатор'}:</label>
              <select
                value={formData.coordinatorName}
                onChange={(e) => setFormData(prev => ({ ...prev, coordinatorName: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-indigo-500 outline-none"
              >
                <option value="">{t('common.notAssigned') || 'Не назначен'}</option>
                {(() => {
                  const filteredUsers = users.filter(u => ['Super Admin', 'Admin', 'Coordinator', 'Project Manager'].includes(u.role));
                  const isInList = filteredUsers.some(u => u.username === formData.coordinatorName);
                  return (
                    <>
                      {formData.coordinatorName && !isInList && (
                        <option value={formData.coordinatorName}>{formData.coordinatorName} ✓</option>
                      )}
                      {filteredUsers.map(user => (
                        <option key={user.id} value={user.username}>{user.username}</option>
                      ))}
                    </>
                  );
                })()}
              </select>
            </div>
              <div>
                <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-gray-400">
                {t('bidding.coordinatorFee') || 'Гонорар координатора'}:
                {(isCustomValue('coordinator_percentage', formData.coordinatorPercentage) || isCustomValue('use_coordinator_fixed_fee', formData.useCoordinatorFixedFee)) && (
                  <span className="ml-1 text-amber-400 text-[10px]">[кастом]</span>
                )}
              </label>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, useCoordinatorFixedFee: !prev.useCoordinatorFixedFee }))}
                    className={`text-xs px-2 py-0.5 rounded ${formData.useCoordinatorFixedFee ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                  >
                    {formData.useCoordinatorFixedFee ? t('bidding.fixedAmount') || 'Фикс.' : '%'}
                  </button>
                </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                    step={formData.useCoordinatorFixedFee ? "0.01" : "0.1"}
                    max={formData.useCoordinatorFixedFee ? undefined : 100}
                    value={(formData.useCoordinatorFixedFee ? formData.coordinatorFee : formData.coordinatorPercentage) || ''}
                    placeholder="0"
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      [formData.useCoordinatorFixedFee ? 'coordinatorFee' : 'coordinatorPercentage']: parseFloat(e.target.value) || 0 
                    }))}
                    className={getInputClass(
                      formData.useCoordinatorFixedFee ? 'coordinator_fee' : 'coordinator_percentage',
                      formData.useCoordinatorFixedFee ? formData.coordinatorFee : formData.coordinatorPercentage,
                      "flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm text-center focus:border-indigo-500 outline-none"
                    )}
                />
                  <span className="text-gray-400 text-sm w-12">{formData.useCoordinatorFixedFee ? currency : '%'}</span>
              </div>
            </div>
          </div>

          {/* Supervisor */}
            <div className="p-3 bg-gray-900/50 rounded-lg border border-gray-700/50 space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('bidding.supervisorName') || 'Имя супервайзера'}:</label>
              <select
                value={formData.supervisorName}
                onChange={(e) => setFormData(prev => ({ ...prev, supervisorName: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-indigo-500 outline-none"
              >
                <option value="">{t('common.notAssigned') || 'Не назначен'}</option>
                {(() => {
                    const filteredUsers = users.filter(u => ['Super Admin', 'Admin', 'Producer', 'Project Manager', 'Coordinator'].includes(u.role));
                  const isInList = filteredUsers.some(u => u.username === formData.supervisorName);
                  return (
                    <>
                      {formData.supervisorName && !isInList && (
                        <option value={formData.supervisorName}>{formData.supervisorName} ✓</option>
                      )}
                      {filteredUsers.map(user => (
                        <option key={user.id} value={user.username}>{user.username}</option>
                      ))}
                    </>
                  );
                })()}
              </select>
            </div>
              <div>
                <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-gray-400">
                {t('bidding.supervisorFee') || 'Гонорар супервайзера'}:
                {(isCustomValue('supervisor_percentage', formData.supervisorPercentage) || isCustomValue('use_supervisor_fixed_fee', formData.useSupervisorFixedFee)) && (
                  <span className="ml-1 text-amber-400 text-[10px]">[кастом]</span>
                )}
              </label>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, useSupervisorFixedFee: !prev.useSupervisorFixedFee }))}
                    className={`text-xs px-2 py-0.5 rounded ${formData.useSupervisorFixedFee ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                  >
                    {formData.useSupervisorFixedFee ? t('bidding.fixedAmount') || 'Фикс.' : '%'}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step={formData.useSupervisorFixedFee ? "0.01" : "0.1"}
                    max={formData.useSupervisorFixedFee ? undefined : 100}
                    value={(formData.useSupervisorFixedFee ? formData.supervisorFee : formData.supervisorPercentage) || ''}
                    placeholder="0"
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      [formData.useSupervisorFixedFee ? 'supervisorFee' : 'supervisorPercentage']: parseFloat(e.target.value) || 0 
                    }))}
                    className={getInputClass(
                      formData.useSupervisorFixedFee ? 'supervisor_fee' : 'supervisor_percentage',
                      formData.useSupervisorFixedFee ? formData.supervisorFee : formData.supervisorPercentage,
                      "flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm text-center focus:border-indigo-500 outline-none"
                    )}
                  />
                  <span className="text-gray-400 text-sm w-12">{formData.useSupervisorFixedFee ? currency : '%'}</span>
                </div>
              </div>
            </div>

            {/* Finder */}
            <div className="p-3 bg-gray-900/50 rounded-lg border border-gray-700/50 space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('bidding.finderName') || 'Finder'}:</label>
                <select
                  value={formData.finderName}
                  onChange={(e) => setFormData(prev => ({ ...prev, finderName: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-indigo-500 outline-none"
                >
                  <option value="">{t('common.notAssigned') || 'Не назначен'}</option>
                  {(() => {
                    const filteredUsers = users.filter(u => ['Producer', 'Manager', 'Super Admin', 'Sales'].includes(u.role));
                    const isInList = filteredUsers.some(u => u.username === formData.finderName);
                    return (
                      <>
                        {formData.finderName && !isInList && (
                          <option value={formData.finderName}>{formData.finderName} ✓</option>
                        )}
                        {filteredUsers.map(user => (
                          <option key={user.id} value={user.username}>{user.username}</option>
                        ))}
                      </>
                    );
                  })()}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-gray-400">
                    {t('bidding.finderFee') || 'Finder Fee'}:
                    {(isCustomValue('finder_percentage', formData.finderPercentage) || isCustomValue('use_finder_fixed_fee', formData.useFinderFixedFee)) && (
                      <span className="ml-1 text-amber-400 text-[10px]">[кастом]</span>
                    )}
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, useFinderFixedFee: !prev.useFinderFixedFee }))}
                    className={`text-xs px-2 py-0.5 rounded ${formData.useFinderFixedFee ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                  >
                    {formData.useFinderFixedFee ? t('bidding.fixedAmount') || 'Фикс.' : '%'}
                  </button>
                </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                    step={formData.useFinderFixedFee ? "0.01" : "0.1"}
                    max={formData.useFinderFixedFee ? undefined : 100}
                    value={(formData.useFinderFixedFee ? formData.finderFee : formData.finderPercentage) || ''}
                    placeholder="0"
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      [formData.useFinderFixedFee ? 'finderFee' : 'finderPercentage']: parseFloat(e.target.value) || 0 
                    }))}
                    className={getInputClass(
                      formData.useFinderFixedFee ? 'finder_fee' : 'finder_percentage',
                      formData.useFinderFixedFee ? formData.finderFee : formData.finderPercentage,
                      "flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm text-center focus:border-indigo-500 outline-none"
                    )}
                />
                  <span className="text-gray-400 text-sm w-12">{formData.useFinderFixedFee ? currency : '%'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Documents & Payment */}
        <div className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg space-y-3">
          <h3 className="text-sm font-semibold text-gray-300 border-b border-gray-700 pb-2">
            {t('bidding.documentsPayment') || 'Documents & Payment'}
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('bidding.invoiceNumber') || 'Invoice #'}</label>
              <input
                type="text"
                name="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:border-indigo-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('bidding.contractNumber') || 'Contract #'}</label>
              <input
                type="text"
                name="contractNumber"
                value={formData.contractNumber}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:border-indigo-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('bidding.paymentDeadline') || 'Payment Deadline'}</label>
              <input
                type="date"
                name="paymentDeadline"
                value={formData.paymentDeadline}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:border-indigo-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('bidding.paymentDate') || 'Payment Date'}</label>
              <input
                type="date"
                name="paymentDate"
                value={formData.paymentDate}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:border-indigo-500 outline-none"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-400 mb-1">{t('bidding.paymentStatus') || 'Payment Status'}</label>
              <select
                name="paymentStatus"
                value={formData.paymentStatus}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white focus:border-indigo-500 outline-none"
              >
                <option value="pending">{t('bidding.status.pending') || 'Pending'}</option>
                <option value="partial">{t('bidding.status.partial') || 'Partial'}</option>
                <option value="paid">{t('bidding.status.paid') || 'Paid'}</option>
                <option value="overdue">{t('bidding.status.overdue') || 'Overdue'}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
          >
            {isSaving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>

    </Modal>

      {/* Re-approval Confirmation Modal - rendered outside main modal for proper z-index */}
      {showReapproveModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="relative bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <button
              onClick={() => setShowReapproveModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {t('bidding.reapproveTitle') || 'Обновить финансы?'}
                </h3>
              </div>
            </div>
            
            <p className="text-gray-300 text-sm mb-6">
              {t('bidding.reapproveMessage') || 'Вы изменили гонорары управления. Чтобы изменения отразились в финансах, необходимо переодобрить смету. Это обновит существующие обязательства.'}
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => saveSettings(false)}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
              >
                {t('bidding.saveOnly') || 'Только сохранить'}
              </button>
              <button
                onClick={() => saveSettings(true)}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                {isSaving ? (t('common.saving') || '...') : (t('bidding.saveAndReapprove') || 'Сохранить и обновить')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BiddingSettingsModal;

